import { useEffect, useRef } from 'react';
import { getIn, useFormikContext } from 'formik';

import { normalizeOptionalString as norm } from './utils';

/**
 * Monitors validated fields after GBG validation and clears the validation
 * status when any field is manually edited (diverges from the validated snapshot).
 *
 * This is a UX helper — the backend is authoritative for revert logic.
 */
export const useFieldsValidationRevert = (
  fieldIds: readonly string[],
  validationFieldPath: string,
  getFullFieldPath: (fieldId: string) => string
): void => {
  const { values, setFieldValue } = useFormikContext();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const fieldsValidation = getIn(values, validationFieldPath);
  const fieldsValidationRef = useRef(fieldsValidation);
  fieldsValidationRef.current = fieldsValidation;

  const validatedFieldsSnapshot = useRef<Record<string, string> | null>(null);

  useEffect(() => {
    if (fieldsValidation) {
      validatedFieldsSnapshot.current = Object.fromEntries(
        fieldIds.map((field) => [field, getIn(valuesRef.current, getFullFieldPath(field)) ?? ''])
      );
    } else {
      validatedFieldsSnapshot.current = null;
    }
  }, [fieldsValidation, fieldIds, getFullFieldPath]);

  // No dependency array — intentionally runs after every render.
  // The checks inside should be cheap (ref reads + a few string comparisons).
  useEffect(() => {
    if (!fieldsValidationRef.current || !validatedFieldsSnapshot.current) return;

    const hasChanged = fieldIds.some(
      (field) =>
        norm(getIn(valuesRef.current, getFullFieldPath(field))) !== norm(validatedFieldsSnapshot.current?.[field])
    );
    if (hasChanged) {
      setFieldValue(validationFieldPath, null);
    }
  });
};
